import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { IOrderReadResourceV18, OrderScenario } from 'components/order/model/Order';
import { ApplicationState } from 'store/store';
import { ActionItem } from './ActionItem';
import { getCurrencyString } from 'lib/helpers';
import { Location } from 'components/location/model/Location';
import { OrderDetails } from './OrderDetails';
import { getOrderDeliveryStatusString, isDeliveryOrderActive } from 'components/delivery/helpers';
import { BaseModal } from 'ui/BaseModal';
import { useResponsive } from 'src/hooks/useResponsive';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const orderScenarioToTitleMap: Record<string, string> = {
    [OrderScenario.ORDER_TO_TABLE]: 'ACTIVITY_ORDER_SCENARIO_OTT',
    [OrderScenario.TABLE]: 'ACTIVITY_ORDER_SCENARIO_PAT',
    [OrderScenario.PREORDER]: 'ACTIVITY_ORDER_SCENARIO_PREORDER',
    [OrderScenario.DINE_IN]: 'ACTIVITY_ORDER_SCENARIO_DINE_IN',
    [OrderScenario.TAB]: 'ACTIVITY_ORDER_SCENARIO_TAB',
    [OrderScenario.DELIVERY]: 'ACTIVITY_ORDER_SCENARIO_DELIVERY'
};

function orderTotalSum(order: IOrderReadResourceV18) {
    if (order) {
        return order.total.payments !== 0
            ? getCurrencyString(order.total.payments, order.currencyCode)
            : getCurrencyString(order.total.total, order.currencyCode);
    }
}

interface IProps {
    list: IOrderReadResourceV18[];
}

export const OrdersList = ({ list }: IProps) => {
    const { t } = useTranslation();
    const { logUserEvent } = useGAHelpers();
    const { isDesktop } = useResponsive();
    const { locations } = useSelector((state: ApplicationState) => state.locations);
    const [selectedOrder, setSelectedOrder] = React.useState<IOrderReadResourceV18 | null>(null);

    const completedOrders = list.filter(order => !order.isOpen && !isDeliveryOrderActive(order));
    const inProgressOrders = list.filter(order => order.isOpen || isDeliveryOrderActive(order));

    const getOrderSubtitle = React.useCallback(
        (order: IOrderReadResourceV18, locations: Location[]) => {
            const date = moment(order.createdAt).fromNow();
            if (order.scenario === OrderScenario.DELIVERY) {
                const statusString = getOrderDeliveryStatusString(order);
                return `${
                    statusString
                        ? t(statusString, { time: moment(order?.delivery?.lastStatusAt).format('h:mm a') })
                        : ''
                } | ${date}`;
            }
            const locationTitle = locations.find(location => location._id === order.locationId)?.title || '';
            return `${locationTitle} | ${date}`;
        },
        [t]
    );

    const handleOrderClick = React.useCallback(
        (order: IOrderReadResourceV18) => () => {
            setSelectedOrder(order);
            logUserEvent(EGAEventName.ActivityOrderTapped);
        },
        [logUserEvent]
    );

    const getOrderTitle = React.useCallback(
        (order: IOrderReadResourceV18) =>
            `${t(orderScenarioToTitleMap[order.scenario])} - ${orderTotalSum(order)}`,
        [t]
    );

    const renderOrderItem = React.useCallback(
        (order: IOrderReadResourceV18) => (
            <ActionItem
                key={order.id}
                title={getOrderTitle(order)}
                subtitle={getOrderSubtitle(order, locations)}
                onClick={handleOrderClick(order)}
            />
        ),
        [getOrderTitle, getOrderSubtitle, locations, handleOrderClick]
    );

    const handleCloseOrderDetails = React.useCallback(() => {
        setSelectedOrder(null);
    }, []);

    return (
        <Box>
            {inProgressOrders.length > 0 && (
                <Box sx={{ paddingTop: 2 }}>
                    <Typography sx={{ paddingLeft: 3.5 }} variant="h6">
                        {t('ACTIVITY_ACTIVE_ORDERS')}
                    </Typography>
                    {inProgressOrders.map(renderOrderItem)}
                </Box>
            )}
            {completedOrders.length > 0 && (
                <Box sx={{ paddingTop: 2 }}>
                    <Typography sx={{ paddingLeft: 3.5 }} variant="h6">
                        {t('ACTIVITY_COMPLETED_ORDERS')}
                    </Typography>
                    {completedOrders.map(renderOrderItem)}
                </Box>
            )}
            <BaseModal
                open={!!selectedOrder}
                onClose={handleCloseOrderDetails}
                noPadding
                scrollable
                fullScreen={!isDesktop}
            >
                <OrderDetails
                    orderId={selectedOrder?.id.toString()}
                    locationId={selectedOrder?.locationId}
                    createdAt={selectedOrder?.createdAt?.toString()}
                    onClose={handleCloseOrderDetails}
                />
            </BaseModal>
        </Box>
    );
};
